import { useEffect } from 'react';
import Spinner from './Spinner';

interface DeliveryModeConfirmModalProps {
  open: boolean;
  targetMode: boolean; // true = Auto-send after 24h, false = Always ask me first
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export default function DeliveryModeConfirmModal({
  open,
  targetMode,
  onConfirm,
  onCancel,
  loading = false,
}: DeliveryModeConfirmModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, loading, onCancel]);

  if (!open) return null;

  const isEnablingAutoSend = targetMode;

  const title = isEnablingAutoSend
    ? 'Enable Auto-Send Follow-ups?'
    : "Switch to 'Always Ask Me First'?";

  const subtitle = isEnablingAutoSend
    ? 'Follow-ups will send automatically after 24 hours if unreviewed.'
    : 'No follow-up emails will ever send without your explicit approval.';

  const infoText = isEnablingAutoSend
    ? "On Day 3 of each outreach cycle, you'll receive an email notification with a preview link. If you don't confirm or skip within 24 hours, the follow-up will be dispatched automatically using your connected Gmail."
    : 'Follow-up drafts will be safely held in your queue until you review them. Automated dispatch after 24 hours will be disabled.';

  const confirmLabel = isEnablingAutoSend
    ? 'Enable Auto-Send'
    : 'Switch to Safe Mode';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity"
        onClick={() => {
          if (!loading) onCancel();
        }}
      />

      {/* Modal Card */}
      <div
        className="relative bg-white rounded-3xl p-6 sm:p-7 max-w-md w-full shadow-2xl border border-gray-100 z-10 animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4 mb-5">
          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
              isEnablingAutoSend ? 'bg-amber-50 text-amber-600' : 'bg-[#ecfdf5] text-[#059669]'
            }`}
          >
            {isEnablingAutoSend ? (
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            )}
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 leading-tight">{title}</h3>
            <p className={`text-xs font-medium mt-1 leading-snug ${isEnablingAutoSend ? 'text-amber-700' : 'text-gray-500'}`}>
              {subtitle}
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div
          className={`rounded-2xl p-4 flex items-start gap-3 mb-6 border ${
            isEnablingAutoSend
              ? 'bg-amber-50/70 border-amber-200/60 text-amber-900'
              : 'bg-[#f0fdf4] border-[#dcfce7] text-gray-700'
          }`}
        >
          <svg
            className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
              isEnablingAutoSend ? 'text-amber-600' : 'text-[#059669]'
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16v-4m0-4h.01" />
          </svg>
          <p className="text-xs sm:text-[13px] leading-relaxed">
            {infoText}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-3 px-4 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-sm transition-colors text-center shadow-sm disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 py-3 px-4 rounded-xl text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2 shadow-sm disabled:opacity-60 ${
              isEnablingAutoSend
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-[#059669] hover:bg-[#047857]'
            }`}
          >
            {loading ? (
              <Spinner size="sm" color="#ffffff" />
            ) : (
              <span>{confirmLabel}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
